// Research-backed relationship assessment questions
// Based on validated frameworks: 7 Dimensions of Relationships, RELATE, Gottman

export interface AssessmentQuestion {
  id: string;
  dimension: string;
  subdimension: string;
  question: string;
  description: string;
  applicableTo: ('romantic' | 'family' | 'friendship' | 'work' | 'other')[];
}

export interface AssessmentDimension {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  applicableTo: ('romantic' | 'family' | 'friendship' | 'work' | 'other')[];
}

export const ASSESSMENT_DIMENSIONS: AssessmentDimension[] = [
  {
    id: 'security',
    name: 'Security & Trust',
    description: 'Feeling safe, prioritised, and committed in the relationship',
    icon: 'Shield',
    color: 'hsl(182, 29%, 26%)', // Deep Teal (primary)
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'connection',
    name: 'Emotional Connection',
    description: 'Affection, attunement, and emotional transparency',
    icon: 'Heart',
    color: 'hsl(182, 37%, 46%)', // Vibrant Teal (accent)
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'autonomy',
    name: 'Healthy Autonomy',
    description: 'Personal satisfaction, emotional regulation, and responsibility',
    icon: 'User',
    color: 'hsl(177, 41%, 76%)', // Light Aqua (muted)
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'communication',
    name: 'Communication',
    description: 'Sharing, conflict resolution, and heartfelt conversation',
    icon: 'MessageCircle',
    color: 'hsl(182, 37%, 46%)', // Vibrant Teal (accent)
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'passion',
    name: 'Passion & Intimacy',
    description: 'Physical connection, desire, and romantic expression',
    icon: 'Flame',
    color: 'hsl(42, 44%, 66%)', // Soft Gold (secondary)
    applicableTo: ['romantic']
  },
  {
    id: 'boundaries',
    name: 'Boundaries & Respect',
    description: 'Respecting differences, maintaining appropriate limits',
    icon: 'CircleDot',
    color: 'hsl(182, 29%, 26%)', // Deep Teal (primary)
    applicableTo: ['family', 'friendship', 'work', 'other']
  },
  {
    id: 'teamwork',
    name: 'Teamwork & Support',
    description: 'Service, shared responsibilities, and flexibility',
    icon: 'Users',
    color: 'hsl(42, 44%, 66%)', // Soft Gold (secondary)
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'growth',
    name: 'Growth & Purpose',
    description: 'Supporting each other\'s best self and shared vision',
    icon: 'Sprout',
    color: 'hsl(182, 37%, 46%)', // Vibrant Teal (accent)
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  }
];

export const ASSESSMENT_QUESTIONS: AssessmentQuestion[] = [
  // SECURITY & TRUST (3 questions)
  {
    id: 'security_priority',
    dimension: 'security',
    subdimension: 'Priority',
    question: 'This relationship is a priority in my life',
    description: 'We resolve issues quickly and turn to each other first in important matters',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'security_safety',
    dimension: 'security',
    subdimension: 'Safety',
    question: 'I feel safe and secure in this relationship',
    description: 'We have each other\'s back and don\'t threaten the relationship during conflict',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'security_commitment',
    dimension: 'security',
    subdimension: 'Commitment',
    question: 'We are both committed to making this relationship work',
    description: 'We understand relationships have ups and downs but stay committed through them',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  
  // EMOTIONAL CONNECTION (3 questions)
  {
    id: 'connection_affection',
    dimension: 'connection',
    subdimension: 'Affection',
    question: 'We regularly show affection and appreciation',
    description: 'We make time to connect and express how much we value each other',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'connection_attunement',
    dimension: 'connection',
    subdimension: 'Attunement',
    question: 'We understand each other\'s feelings and perspectives',
    description: 'We can usually sense what the other feels and know how to support them',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'connection_vulnerability',
    dimension: 'connection',
    subdimension: 'Vulnerability',
    question: 'We can share vulnerable feelings without fear of judgment',
    description: 'We feel comfortable sharing fears, doubts, and insecurities',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  
  // HEALTHY AUTONOMY (3 questions)
  {
    id: 'autonomy_satisfaction',
    dimension: 'autonomy',
    subdimension: 'Personal Satisfaction',
    question: 'We both maintain our individual identities and interests',
    description: 'We have a good balance between togetherness and personal pursuits',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'autonomy_regulation',
    dimension: 'autonomy',
    subdimension: 'Emotional Regulation',
    question: 'We can manage our emotions during disagreements',
    description: 'We stay calm even when the other is upset and can express differing opinions',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'autonomy_responsibility',
    dimension: 'autonomy',
    subdimension: 'Responsibility',
    question: 'We take responsibility for our own part in conflicts',
    description: 'We ask "What can I do differently?" rather than only blaming the other',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  
  // COMMUNICATION (3 questions)
  {
    id: 'communication_sharing',
    dimension: 'communication',
    subdimension: 'Sharing',
    question: 'We communicate openly about important matters',
    description: 'We check in regularly and can talk about anything including difficult topics',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'communication_conflict',
    dimension: 'communication',
    subdimension: 'Conflict Resolution',
    question: 'We handle disagreements respectfully and constructively',
    description: 'We address issues promptly, avoid blame, and usually feel closer after difficult talks',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'communication_listening',
    dimension: 'communication',
    subdimension: 'Listening',
    question: 'We truly listen to understand, not just to respond',
    description: 'We respect different perspectives and prioritise connection over being right',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  
  // PASSION & INTIMACY (3 questions - romantic only)
  {
    id: 'passion_sensuality',
    dimension: 'passion',
    subdimension: 'Sensuality',
    question: 'We enjoy physical affection and intimacy together',
    description: 'We value touch, closeness, and sensual connection',
    applicableTo: ['romantic']
  },
  {
    id: 'passion_openness',
    dimension: 'passion',
    subdimension: 'Openness',
    question: 'We can discuss our needs and desires openly',
    description: 'We feel safe to share preferences without criticism or shame',
    applicableTo: ['romantic']
  },
  {
    id: 'passion_priority',
    dimension: 'passion',
    subdimension: 'Priority',
    question: 'Physical intimacy remains a priority in our relationship',
    description: 'We make effort to maintain romance and desire',
    applicableTo: ['romantic']
  },
  
  // BOUNDARIES & RESPECT (3 questions - non-romantic)
  {
    id: 'boundaries_respect',
    dimension: 'boundaries',
    subdimension: 'Respect',
    question: 'We respect each other\'s boundaries and limits',
    description: 'We understand and honour what the other needs for comfort',
    applicableTo: ['family', 'friendship', 'work', 'other']
  },
  {
    id: 'boundaries_differences',
    dimension: 'boundaries',
    subdimension: 'Differences',
    question: 'We accept our differences without trying to change each other',
    description: 'We appreciate that we don\'t need to agree on everything',
    applicableTo: ['family', 'friendship', 'work', 'other']
  },
  {
    id: 'boundaries_appropriate',
    dimension: 'boundaries',
    subdimension: 'Appropriate Limits',
    question: 'The relationship has healthy boundaries that work for both of us',
    description: 'We maintain appropriate expectations and don\'t overstep',
    applicableTo: ['family', 'friendship', 'work', 'other']
  },
  
  // TEAMWORK & SUPPORT (3 questions)
  {
    id: 'teamwork_service',
    dimension: 'teamwork',
    subdimension: 'Service',
    question: 'We enjoy helping and supporting each other',
    description: 'We are generous with our time and effort without keeping score',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'teamwork_balance',
    dimension: 'teamwork',
    subdimension: 'Balance',
    question: 'There is a fair balance in our relationship',
    description: 'We share responsibilities and support each other when needed',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'teamwork_flexibility',
    dimension: 'teamwork',
    subdimension: 'Flexibility',
    question: 'We can be flexible and find compromises',
    description: 'We don\'t hold rigidly to our way and strive for win-win solutions',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  
  // GROWTH & PURPOSE (3 questions)
  {
    id: 'growth_bestself',
    dimension: 'growth',
    subdimension: 'Best Self',
    question: 'We support each other in becoming our best selves',
    description: 'We encourage growth and support each other\'s dreams and aspirations',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'growth_vision',
    dimension: 'growth',
    subdimension: 'Shared Vision',
    question: 'We share important values and life direction',
    description: 'We have compatible goals and engage in meaningful activities together',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  },
  {
    id: 'growth_purpose',
    dimension: 'growth',
    subdimension: 'Purpose',
    question: 'This relationship adds meaning and purpose to my life',
    description: 'We help each other grow and see the bigger picture',
    applicableTo: ['romantic', 'family', 'friendship', 'work', 'other']
  }
];

export function getQuestionsForRelationshipType(
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other'
): AssessmentQuestion[] {
  return ASSESSMENT_QUESTIONS.filter(q => q.applicableTo.includes(relationshipType));
}

export function getDimensionsForRelationshipType(
  relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other'
): AssessmentDimension[] {
  return ASSESSMENT_DIMENSIONS.filter(d => d.applicableTo.includes(relationshipType));
}

export function getScoreInterpretation(score: number): {
  level: 'very_high' | 'high' | 'moderate' | 'low' | 'very_low';
  label: string;
  description: string;
  color: string;
} {
  if (score >= 18) {
    return {
      level: 'very_high',
      label: 'Thriving',
      description: 'This is a real strength in your relationship.',
      color: 'hsl(182, 37%, 46%)' // Vibrant Teal
    };
  } else if (score >= 14) {
    return {
      level: 'high',
      label: 'Healthy',
      description: 'You\'re in a good place with some room to grow.',
      color: 'hsl(177, 41%, 76%)' // Light Aqua
    };
  } else if (score >= 10) {
    return {
      level: 'moderate',
      label: 'Growing',
      description: 'There\'s both satisfaction and opportunity here.',
      color: 'hsl(42, 44%, 66%)' // Soft Gold
    };
  } else if (score >= 6) {
    return {
      level: 'low',
      label: 'Needs Attention',
      description: 'This area could benefit from focused effort.',
      color: 'hsl(42, 44%, 56%)' // Darker Gold
    };
  } else {
    return {
      level: 'very_low',
      label: 'Priority Area',
      description: 'This is an important area to address together.',
      color: 'hsl(182, 29%, 26%)' // Deep Teal
    };
  }
}
